import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

// Custom SVG Icons
const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="3" y="5" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M3 8h14M6 3v3m8-3v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SpacesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="4" y="6" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M7 6v8m6-8v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const GroupsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="7" cy="6" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="13" cy="6" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M10 14a4 4 0 0 0-4 4m8 0a4 4 0 0 0-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="5" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M15 16v-1a3 3 0 0 0-3-3H8a3 3 0 0 0-3 3v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SyncIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M17 10a7 7 0 0 1-7 7m-7-7a7 7 0 0 1 7-7m-7 7h3m11 0h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="m14 7 3-3-3-3m-8 16-3-3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DashboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="3" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="11" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="3" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="11" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const TimetableIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="3" y="4" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M3 8h14M7 11h6M7 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M6 3v3m8-3v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const CalendarNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation("global");
  const location = useLocation();

  const menuItems = [
    { path: 'timetable', translationKey: 'calendar.menu.timetable', icon: <TimetableIcon /> },
    { path: 'spaces', translationKey: 'calendar.menu.spaceManagement', icon: <SpacesIcon /> },
    // { path: 'groups', translationKey: 'calendar.menu.groupsManagement', icon: <GroupsIcon /> },
    { path: 'users', translationKey: 'calendar.menu.usersManagement', icon: <UsersIcon /> },
    { path: 'sync', translationKey: 'calendar.menu.sync', icon: <SyncIcon /> },
    { path: '/dashboard/home', translationKey: 'calendar.menu.dashboard', icon: <DashboardIcon /> }
  ];

  const toggleAccordion = () => setIsOpen(!isOpen);

  React.useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <nav className={styles.calendarNav} aria-label="Calendar navigation">
      <div className={styles.accordionItem}>
        <button 
          className={styles.accordionHeader}
          onClick={toggleAccordion}
          aria-expanded={isOpen}
          aria-controls="calendar-nav-content"
        >
          <span className={styles.headerContent}>
            <span className={styles.iconWrapper}>
              <CalendarIcon />
            </span>
            <span className={styles.title}>{t('calendar.menu.title')}</span>
          </span>
          <span className={`${styles.arrow} ${isOpen ? styles.arrowUp : styles.arrowDown}`} />
        </button>
        
        <div 
          id="calendar-nav-content"
          className={`${styles.accordionContent} ${isOpen ? styles.open : ''}`}
          aria-hidden={!isOpen}
        >
          <ul className={styles.menuList}>
            {menuItems.map((item) => (
              <li key={item.path} className={styles.menuItem}>
                <NavLink 
                  to={item.path}
                  className={({ isActive }) => 
                    `${styles.menuLink} ${isActive ? styles.active : ''}`
                  }
                  end
                >
                  <span className={styles.menuIcon}>{item.icon}</span>
                  <span className={styles.menuText}>{t(item.translationKey)}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default CalendarNavbar;